/**
 * Hero Video
 *
 * Marketing media block for the Losi Nexus hero.
 *
 * Primary responsibilities:
 * - Defer the demo video until the hero is in view
 * - Keep a designed poster state visible before video playback
 *
 * Dependencies:
 * - React hooks
 * - lib/utils
 */

'use client'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface HeroVideoProps {
  className?: string
}

const previewHighlights = [
  'Calls covered after hours',
  'Website leads routed faster',
  'Shared notes stay in Spaces'
] as const

const previewStats = [
  { label: 'Calls', value: '24/7' },
  { label: 'Chat', value: '<2m' },
  { label: 'Follow-up', value: 'Auto' }
] as const

export const HeroVideo = ({ className = '' }: HeroVideoProps) => {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const [loadVideoAllowed, setLoadVideoAllowed] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleCallbackRef = useRef<number | null>(null)

  useEffect(() => {
    setIsClient(true)

    const checkDarkMode = () => {
      const html = document.documentElement
      setIsDarkMode(html.classList.contains('dark'))
    }

    checkDarkMode()

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkDarkMode()
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isClient || !containerRef.current) return

    const el = containerRef.current
    const allowVideoLoad = () => {
      setLoadVideoAllowed(true)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (!entry?.isIntersecting) return

        setIsInView(true)

        observer.disconnect()

        if (!loadTimeoutRef.current) {
          loadTimeoutRef.current = setTimeout(allowVideoLoad, 1400)
        }

        if (typeof requestIdleCallback !== 'undefined' && idleCallbackRef.current === null) {
          idleCallbackRef.current = requestIdleCallback(allowVideoLoad, {
            timeout: 1400
          })
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    )

    observer.observe(el)

    return () => {
      observer.disconnect()

      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current)
        loadTimeoutRef.current = null
      }

      if (idleCallbackRef.current !== null) {
        cancelIdleCallback(idleCallbackRef.current)
        idleCallbackRef.current = null
      }
    }
  }, [isClient])

  useEffect(() => {
    if (!isInView || !isClient || !videoRef.current) return

    const video = videoRef.current
    video.loop = true
    video.muted = true
    video.playsInline = true

    const playVideo = async () => {
      try {
        await video.play()
      } catch {
        // Autoplay blocked; video will show poster/placeholder
      }
    }

    playVideo()
    return () => {
      video.pause()
    }
  }, [isInView, isClient, isDarkMode])

  const previewSurfaceClassName = isDarkMode
    ? 'border-white/10 bg-[linear-gradient(180deg,rgba(10,16,24,0.98),rgba(5,9,15,0.98))] text-slate-50'
    : 'border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(245,247,250,0.96))] text-slate-950'

  const previewPanelClassName = isDarkMode
    ? 'border-white/10 bg-white/[0.04]'
    : 'border-slate-200/90 bg-white/88'
  const previewInsetRowClassName = isDarkMode
    ? 'border-white/10 bg-white/5'
    : 'border-slate-200/80 bg-slate-50/90'

  const previewMutedTextClassName = isDarkMode ? 'text-slate-300' : 'text-slate-600'
  const previewSoftTextClassName = isDarkMode ? 'text-slate-400' : 'text-slate-500'
  const previewBadgeClassName = isDarkMode
    ? 'border-sky-400/20 bg-sky-400/10 text-sky-100'
    : 'border-sky-200 bg-sky-50 text-sky-700'

  if (!isClient) {
    return (
      <div
        className={cn('relative w-full overflow-hidden rounded-[2rem] bg-muted/50', className)}
        style={{ aspectRatio: '16/9' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-muted/30 to-muted/50" />
      </div>
    )
  }

  const videoSrc = isDarkMode ? '/NEXUS-DEMO-BLACK.mp4' : '/NEXUS-DEMO-WHITE.mp4'

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-white/72 relative w-full overflow-hidden rounded-[2rem] border border-slate-200/80 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[rgba(7,11,18,0.86)] dark:shadow-[0_24px_70px_rgba(0,0,0,0.34)]',
        className
      )}
    >
      <div
        aria-hidden
        className={cn(
          'absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(88,124,180,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(12,18,28,0.18),transparent_44%)]',
          isDarkMode
            ? 'bg-[radial-gradient(circle_at_top_left,rgba(84,128,198,0.2),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(8,12,18,0.28),transparent_44%)]'
            : undefined
        )}
      />

      <div className="absolute inset-2 sm:inset-3" aria-hidden>
        <div
          className={cn(
            'flex h-full flex-col overflow-hidden rounded-[1.7rem] border shadow-[0_18px_45px_rgba(15,23,42,0.08)] dark:shadow-[0_18px_45px_rgba(0,0,0,0.34)]',
            previewSurfaceClassName
          )}
        >
          <div
            className={cn(
              'flex items-center justify-between border-b px-4 py-3',
              isDarkMode ? 'border-white/10' : 'border-slate-200/90'
            )}
          >
            <div className="flex flex-wrap gap-2">
              {['Calls', 'Chat', 'Follow-up'].map((label) => (
                <span
                  key={label}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]',
                    previewBadgeClassName
                  )}
                >
                  {label}
                </span>
              ))}
            </div>
            <span
              className={cn(
                'text-[10px] font-semibold uppercase tracking-[0.22em]',
                previewSoftTextClassName
              )}
            >
              Nexus live preview
            </span>
          </div>

          <div className="grid flex-1 gap-3 p-4 md:grid-cols-[1.08fr_0.92fr]">
            <div className={cn('rounded-[1.45rem] border p-4', previewPanelClassName)}>
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'flex size-10 items-center justify-center rounded-full text-sm font-semibold',
                    isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-950 text-white'
                  )}
                >
                  L
                </div>
                <div>
                  <p
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-[0.18em]',
                      previewSoftTextClassName
                    )}
                  >
                    Active workflow
                  </p>
                  <p className="mt-1 text-sm font-semibold">Front desk overflow is covered</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3">
                {previewHighlights.map((highlight, index) => (
                  <div
                    key={highlight}
                    className={cn('rounded-[1.15rem] border px-4 py-3', previewPanelClassName)}
                  >
                    <p
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-[0.18em]',
                        previewSoftTextClassName
                      )}
                    >
                      Step {index + 1}
                    </p>
                    <p className={cn('mt-1 text-sm leading-6', previewMutedTextClassName)}>
                      {highlight}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-3">
              <div className={cn('rounded-[1.45rem] border p-4', previewPanelClassName)}>
                <p
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-[0.18em]',
                    previewSoftTextClassName
                  )}
                >
                  This hour
                </p>
                <div className="mt-3 grid gap-2">
                  <div
                    className={cn(
                      'flex items-center justify-between rounded-2xl border px-3 py-2',
                      previewInsetRowClassName
                    )}
                  >
                    <span className={cn('text-sm', previewMutedTextClassName)}>Booked calls</span>
                    <span className="text-sm font-semibold">12</span>
                  </div>
                  <div
                    className={cn(
                      'flex items-center justify-between rounded-2xl border px-3 py-2',
                      previewInsetRowClassName
                    )}
                  >
                    <span className={cn('text-sm', previewMutedTextClassName)}>
                      Qualified chats
                    </span>
                    <span className="text-sm font-semibold">8</span>
                  </div>
                  <div
                    className={cn(
                      'flex items-center justify-between rounded-2xl border px-3 py-2',
                      previewInsetRowClassName
                    )}
                  >
                    <span className={cn('text-sm', previewMutedTextClassName)}>
                      Team notes synced
                    </span>
                    <span className="text-sm font-semibold">19</span>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-1">
                {previewStats.map((stat) => (
                  <div
                    key={stat.label}
                    className={cn('rounded-[1.3rem] border p-4', previewPanelClassName)}
                  >
                    <p
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-[0.18em]',
                        previewSoftTextClassName
                      )}
                    >
                      {stat.label}
                    </p>
                    <p className="mt-2 text-xl font-semibold tracking-tight">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isInView && loadVideoAllowed && (
        <video
          ref={videoRef}
          key={isDarkMode ? 'dark' : 'light'}
          src={videoSrc}
          className="absolute inset-0 size-full object-cover object-center"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          aria-label="Nexus AI agent platform demo"
        >
          <track kind="captions" src="" srcLang="en" label="English" />
        </video>
      )}
      <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/50 dark:ring-white/10" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10 dark:to-black/20" />
    </div>
  )
}
